import axios, { type AxiosRequestTransformer } from 'axios';
import { createHttpClient, type TokenStore } from '@/lib/api-client';
import type { AuthTokens } from '@/lib/shared-types';

const STORAGE_KEY = 'venue.tokens';
const TENANT_KEY = 'venue.tenant';

/** Shared with `guestSlice`, which is the only writer of these two. */
export const GUEST_SESSION_KEY = 'venue.guest.session';
export const GUEST_CART_KEY = 'venue.guest.cart';

/**
 * A QR guest has no account. `POST /sessions/qr` hands back a guest
 * session token (`typ: 'guest'`), and from then on it is the only credential
 * this app holds: it goes in the `Authorization` header on every call and in
 * the socket handshake. It is stored in the same slot as a staff token so the
 * shared axios client needs no special case.
 */
export const tokenStore: TokenStore = {
  get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthTokens) : null;
    } catch {
      return null;
    }
  },
  set(tokens) {
    if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(STORAGE_KEY);
  },
};

/** A guest token cannot be refreshed — losing it means scanning again. */
export function setGuestToken(token: string | null): void {
  tokenStore.set(token ? { accessToken: token, refreshToken: '' } : null);
}

/**
 * The tenant comes from the header when nobody is logged in. The QR
 * scan is what tells us which tenant we are in, so it is remembered here and
 * attached to every later request (the guest menu is resolved by tenant).
 */
export function setTenantId(tenantId: string | null): void {
  if (tenantId) localStorage.setItem(TENANT_KEY, tenantId);
  else localStorage.removeItem(TENANT_KEY);
}

export const getTenantId = (): string | null => localStorage.getItem(TENANT_KEY);

/**
 * Forget the table entirely. The persisted session has to go with the token:
 * keeping it would send a guest with a dead credential straight back from
 * /scan to /menu, into a redirect loop on the next 401.
 */
export function clearGuestState(): void {
  tokenStore.set(null);
  setTenantId(null);
  localStorage.removeItem(GUEST_SESSION_KEY);
  localStorage.removeItem(GUEST_CART_KEY);
}

/**
 * Marker header a QR open sets on itself.
 *
 * `POST /sessions/qr` is the one request that must carry **no** remembered
 * identity: the token in the QR resolves its own tenant, and sending the
 * previous venue's `X-Tenant-Id` made `openFromQr` 404 on a tenant mismatch —
 * so a guest holding venue A's storage could never open venue B. The stale
 * guest bearer token is dropped for the same reason: it names a session at a
 * different table.
 */
const FRESH_SCAN_HEADER = 'X-Fresh-Scan';

export const api = createHttpClient({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  tokenStore,
  locale: localStorage.getItem('venue.locale') ?? 'en',
  onUnauthorized: () => {
    // There is no login here: the way back in is the table QR. The reload also
    // guarantees the socket is rebuilt rather than left on a dead identity.
    clearGuestState();
    if (!location.pathname.startsWith('/scan')) location.href = '/scan';
  },
});

// The shared client takes a fixed tenant at construction; a guest only learns
// its tenant after the scan, so the header is attached per request instead.
api.interceptors.request.use((config) => {
  if (config.headers.get(FRESH_SCAN_HEADER)) {
    config.headers.delete(FRESH_SCAN_HEADER);
    config.headers.delete('X-Tenant-Id');
  } else {
    const tenantId = getTenantId();
    if (tenantId) config.headers.set('X-Tenant-Id', tenantId);
  }
  config.headers.set('x-lang', localStorage.getItem('venue.locale') ?? 'en');
  return config;
});

/**
 * The last thing that touches a fresh scan's headers.
 *
 * **Axios runs request interceptors last-registered-first.** `createHttpClient`
 * registers its bearer interceptor inside the factory, so it runs *after* the
 * one above: an `Authorization` delete there removes a header that has not been
 * attached yet, and the shared interceptor then puts the stale guest token
 * back. `TenantMiddleware` prefers any bearer over the header, so that token
 * reintroduced the cross-venue 404 above, fixed identically in
 * `mobile/customer-app`.
 *
 * `transformRequest` runs after every request interceptor and immediately
 * before the adapter, which makes it the only seam that can have the last word.
 * The default transformers are kept after it so the body is still serialised.
 */
const stripIdentity: AxiosRequestTransformer = (data, headers) => {
  headers.delete('Authorization');
  headers.delete('X-Tenant-Id');
  headers.delete(FRESH_SCAN_HEADER);
  return data;
};

const defaultTransforms = (): AxiosRequestTransformer[] => {
  const configured = axios.defaults.transformRequest;
  if (!configured) return [];
  return Array.isArray(configured) ? [...configured] : [configured];
};

/**
 * Open a table from its QR token, carrying nothing from whatever table this
 * device was on before. The only identity in this request is the token itself.
 */
export function openQrRequest(body: { qrToken: string; pin?: string }) {
  return api.post('/sessions/qr', body, {
    headers: { [FRESH_SCAN_HEADER]: '1' },
    transformRequest: [stripIdentity, ...defaultTransforms()],
  });
}
